import * as THREE from "three";
import { getSceneActions, getSceneState } from "../stores/sceneStore";
import { logger } from "../utils/logger";
import type { BodyUUID, Scene } from "../types/scene";

export function randomizeBodyPoseXZYaw(args: {
  bodyUuid: BodyUUID;
  /** Max radial offset in XZ plane (meters). */
  radiusXY: number;
  /** Total yaw range in radians (sampled uniformly in [-yawRange/2, +yawRange/2]). */
  yawRangeRad: number;
}): boolean {
  const scene = getSceneState();
  const body = scene.bodies?.[args.bodyUuid];
  if (!body || !Array.isArray(body.position)) return false;
  const x0 = Number(body.position[0]);
  const y0 = Number(body.position[1]);
  const z0 = Number(body.position[2]);
  if (![x0, y0, z0].every((n) => Number.isFinite(n))) return false;

  const r = Math.max(0, Number(args.radiusXY) || 0) * Math.sqrt(Math.random());
  const th = (Math.random() - 0.5) * 2 * Math.PI;
  const nx = x0 + r * Math.cos(th);
  const nz = z0 + r * Math.sin(th);
  const yawHalf = Math.max(0, Number(args.yawRangeRad) || 0) / 2;
  const yaw = (Math.random() * 2 - 1) * yawHalf;
  const q = new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(0, 1, 0), yaw);
  const quat: [number, number, number, number] = [q.w, q.x, q.y, q.z];
  getSceneActions().setBodyTransform(args.bodyUuid, [nx, y0, nz], quat);
  return true;
}

export function setBodyPose(args: {
  bodyUuid: BodyUUID;
  position: [number, number, number];
  orientation?: [number, number, number, number];
}): boolean {
  const scene = getSceneState();
  if (!scene.bodies?.[args.bodyUuid]) return false;
  const orient =
    Array.isArray(args.orientation) && args.orientation.length === 4
      ? (args.orientation as [number, number, number, number])
      : ([1, 0, 0, 0] as [number, number, number, number]);
  getSceneActions().setBodyTransform(args.bodyUuid, args.position, orient);
  return true;
}

export function cloneSceneSnapshotForBaseline(): unknown {
  try {
    const s = getSceneState() as Record<string, unknown>;
    const { actions: _omit, ...rest } = s;
    void _omit;
    return JSON.parse(JSON.stringify(rest));
  } catch {
    return null;
  }
}

export function restoreBaselineSnapshot(snapshot: unknown): boolean {
  if (!snapshot || typeof snapshot !== "object") return false;
  try {
    getSceneActions().restoreState(snapshot as Scene);
    return true;
  } catch (e) {
    logger.warn("[behavior] restoreBaselineSnapshot failed", e);
    return false;
  }
}
